/*
 * Colorway swatches. Loaded (deferred) by layout/theme.liquid only when settings.colorways_enable is on.
 *
 * <colorway-swatches> modes
 *   card     rendered by snippets/colorway-swatches.liquid inside theme product cards
 *            (snippets/product-grid-item.liquid: collection sections, recommendations, DY cards)
 *   pdp      the product page block: colour name, size carry-over, and the other colours' pages
 *            prefetched in the background on hover, press or keyboard focus
 *   hydrate  the empty element snippets/colorway-boost-bootstrap.liquid adds to every Boost card,
 *            filled from the Section Rendering endpoint /products/{handle}?section_id=colorway-swatches
 *
 * Boost requests: about one per colourway group, not one per card.
 *   - every member of a group stores the same list, so one answer serves every card in it
 *     (memory + sessionStorage, 10 minutes); an answer without a row is cached for that handle only
 *   - cards sharing a SKU_ tag (data-key) queue behind each other and reuse the answers
 *   - lazy (IntersectionObserver, 400px margin), at most 3 requests in flight
 *   - 429 / 403 / 5xx / bot challenge: all requests pause (Retry-After, else 30 s x strikes); after
 *     3 strikes they stop for this page (rows stay empty, the cards still work)
 *
 * Card taps (settings.colorways_card_action 'preview'): a tap shows that colour on the card (image,
 * title, price, link) and a second tap opens it; a mouse hover previews and a click opens. DY cards
 * (<remote-product-card>) re-render as the tapped colour. 'link': swatches are plain links.
 */
(function () {
  'use strict';
  var S = window.theme && window.theme.settings && window.theme.settings.colorways;
  if (!S || window.__colorwaysLoaded) return;
  window.__colorwaysLoaded = true;

  var CFG = {
    ver: 'cw1:' + (S.cacheKey || ''),
    ttl: 10 * 60 * 1000,
    maxInFlight: 3,
    rootMargin: '400px 0px',
    pauseMs: S.pauseMs > 0 ? S.pauseMs : 30000, // not a theme setting; the smoke test shortens it
    maxStrikes: 3,
    hideSoldOut: S.soldOut !== 'strike',
    preview: S.cardAction !== 'link',
    currentFirst: S.cardOrder === 'current_first',
    hoverPrefetchMs: 80, // product page: how long the mouse rests on a swatch before its page is prefetched
  };
  var SPECULATION = !!(window.HTMLScriptElement && HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules'));
  var ROOT = ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/').replace(/\/?$/, '/');
  var slice = Array.prototype.slice;

  /* ------------------------------------------------------------------ cache */
  // g:<first handle> answer · m:<handle> pointer to the answer listing it · h:<handle> the fetched
  // handle's own answer · k:<SKU_ key> pointer to the first answer seen for that key
  var mem = new Map();
  function fresh(v) { return v && Date.now() - v.t <= CFG.ttl ? v : null; }
  function raw(k) {
    var v = fresh(mem.get(k));
    if (v) return v;
    try { v = fresh(JSON.parse(sessionStorage.getItem(CFG.ver + '|' + k))); } catch (e) { v = null; }
    if (v) mem.set(k, v);
    return v;
  }
  function get(k) {
    var v = raw(k);
    return v && v.ref ? raw(v.ref) : v;
  }
  function put(k, v) {
    mem.set(k, v);
    try { sessionStorage.setItem(CFG.ver + '|' + k, JSON.stringify(v)); } catch (e) { /* quota, private mode */ }
  }
  function store(answer, handle, key) {
    answer.t = Date.now();
    if (!answer.handles) return put('h:' + handle, answer);
    var g = 'g:' + answer.handles[0];
    var ptr = { t: answer.t, ref: g };
    put(g, answer);
    answer.handles.forEach(function (h) { put('m:' + h, ptr); });
    put('h:' + handle, ptr);
    if (key) put('k:' + key, ptr);
  }
  function lookup(handle) { return get('m:' + handle) || get('h:' + handle); }
  try { // drop entries written by an older asset, swatch setting, currency or language
    for (var i = sessionStorage.length - 1; i >= 0; i--) {
      var k = sessionStorage.key(i);
      if (k && k.indexOf('cw1:') === 0 && k.indexOf(CFG.ver + '|') !== 0) sessionStorage.removeItem(k);
    }
  } catch (e) { /* storage unavailable */ }

  /* ------------------------------------------------------------------ queue */
  var active = 0, maxActive = 0, queue = [], pausedUntil = 0, strikes = 0, timer = 0;
  function schedule(task) {
    return new Promise(function (resolve, reject) {
      queue.push({ task: task, resolve: resolve, reject: reject });
      pump();
    });
  }
  function pump() {
    if (strikes >= CFG.maxStrikes) {
      while (queue.length) queue.shift().reject(new Error('colorways: requests stopped'));
      return;
    }
    var wait = pausedUntil - Date.now();
    if (wait > 0) {
      if (!timer) timer = setTimeout(function () { timer = 0; pump(); }, wait);
      return;
    }
    while (active < CFG.maxInFlight && queue.length) run(queue.shift());
  }
  function run(job) {
    active++;
    maxActive = Math.max(maxActive, active);
    job.task().then(function (v) {
      strikes = 0;
      job.resolve(v);
    }, function (e) {
      if (!e || !e.retry) return job.reject(e);
      strikes++;
      pausedUntil = Date.now() + Math.max((e.retryAfter || 0) * 1000, CFG.pauseMs * strikes);
      if (strikes >= CFG.maxStrikes) job.reject(e); else queue.unshift(job);
    }).then(function () { active--; pump(); });
  }
  function retryable(r) {
    var e = new Error('colorways: ' + (r ? r.status : 'challenge'));
    e.retry = true;
    e.retryAfter = r ? parseInt(r.headers.get('Retry-After'), 10) || 0 : 0;
    return e;
  }
  function fetchAnswer(handle) {
    var url = ROOT + 'products/' + encodeURIComponent(handle) + '?section_id=colorway-swatches';
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (r.status === 429 || r.status === 403 || r.status >= 500) throw retryable(r);
      if (!r.ok) return { handles: null }; // 404: unpublished or renamed
      return r.text().then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        if (!doc.querySelector('[id^="shopify-section-"]')) throw retryable(null); // bot challenge page
        var root = doc.querySelector('colorway-swatches[data-cw-endpoint]');
        var handles = root ? (root.getAttribute('data-handles') || '').split(',').filter(Boolean) : [];
        return handles.length > 1 ? { handles: handles, html: root.innerHTML } : { handles: null };
      });
    });
  }

  var inflight = new Map(); // slot -> promise
  function resolve(handle, key) {
    var hit = lookup(handle);
    if (hit) return Promise.resolve(hit);
    // Cards of one SKU_ key wait for the first answer, which normally lists them all. Cards missing
    // from it (excluded product, stale list) then fetch their own handle, one at a time per key, so
    // each answer can cover the next card.
    var slot = key ? (get('k:' + key) ? 'x:' : 'k:') + key : 'h:' + handle;
    var waiting = inflight.get(slot);
    if (waiting) return waiting.then(function () { return resolve(handle, key); });
    var p = schedule(function () {
      var again = lookup(handle); // answered while this job was queued
      if (again) return Promise.resolve(again);
      return fetchAnswer(handle).then(function (answer) { store(answer, handle, key); return answer; });
    });
    inflight.set(slot, p);
    var clear = function () { if (inflight.get(slot) === p) inflight.delete(slot); };
    p.then(clear, clear);
    return p;
  }

  /* ------------------------------------------------------- card decoration */
  // The endpoint answer is the same for the whole group: mark this card's product, apply the
  // sold-out and order settings, set the +N link.
  function decorate(el, handle, url) {
    var list = el.querySelector('.colorways__list');
    if (!list) return 0;
    var current = null;
    slice.call(list.children).forEach(function (li) {
      var a = li.querySelector('.colorways__swatch');
      if (!a) return;
      if (a.getAttribute('data-handle') === handle) {
        a.classList.add('is-current');
        a.setAttribute('aria-current', 'true');
        current = li;
      } else if (CFG.hideSoldOut && a.classList.contains('is-sold-out')) {
        li.remove();
      }
    });
    if (current && CFG.currentFirst) list.prepend(current);
    var n = list.children.length;
    var more = el.querySelector('.colorways__more') || el.appendChild(document.createElement('a'));
    more.className = 'colorways__more';
    more.href = url || ROOT + 'products/' + handle;
    more.setAttribute('aria-label', n + ' colors');
    [3, 4, 6].forEach(function (k) { more.setAttribute('data-more-' + k, n > k ? '+' + (n - k) : ''); });
    el.setAttribute('data-count', n);
    return n;
  }

  /* -------------------------------------------------------- card preview */
  function span(cls, value) {
    var s = document.createElement('span');
    if (cls) s.className = cls;
    s.textContent = value;
    return s;
  }
  function swapChildren(el, nodes) { // returns the removed children so they can be put back
    var old = slice.call(el.childNodes);
    el.textContent = '';
    nodes.forEach(function (n) { el.appendChild(n); });
    return old;
  }
  // Each card type: the nodes a preview changes, and the price markup that card uses.
  function themeCard(card) {
    var img = card.querySelector('.grid-product__image-mask img');
    var link = card.querySelector('a.grid-product__link');
    var title = card.querySelector('.grid-product__title');
    if (!img || !link || !title) return null;
    return {
      img: img, title: title, links: [link],
      second: card.querySelector('.grid-product__secondary-image'),
      price: card.querySelector('.grid-product__price'),
      priceNodes: function (a) {
        var nodes = [];
        if (a.dataset.compare) nodes.push(span('grid-product__price--original', a.dataset.compare), document.createTextNode(' '));
        nodes.push(document.createTextNode(a.dataset.price));
        return nodes;
      },
    };
  }
  function boostCard(card) {
    var img = card.querySelector('.boost-sd__product-image-img--main');
    var links = slice.call(card.querySelectorAll('a.boost-sd__product-link'));
    var title = card.querySelector('.boost-sd__product-title');
    if (!img || !links.length || !title) return null;
    return {
      img: img, title: title, links: links,
      second: card.querySelector('.boost-sd__product-image-img--second'),
      price: card.querySelector('.boost-sd__product-price-wrapper'),
      priceNodes: function (a) { // same markup as Boost's productPrice template
        var sale = !!a.dataset.compare;
        var content = span('boost-sd__product-price-content boost-sd__product-price-content--text-align-left', '');
        content.setAttribute('aria-hidden', 'true');
        var now = span(sale ? 'boost-sd__product-price--sale' : 'boost-sd__product-price--default', '');
        now.appendChild(span('boost-sd__format-currency', a.dataset.price));
        content.appendChild(now);
        if (sale) {
          var was = span('boost-sd__format-currency boost-sd__format-currency--price-compare boost-sd__product-price--compare', '');
          was.appendChild(span('boost-sd__format-currency', a.dataset.compare));
          content.appendChild(was);
        }
        var label = sale ? 'Regular price ' + a.dataset.compare + ', now on sale for ' + a.dataset.price : 'Regular price ' + a.dataset.price;
        return [span('boost-sd__visually-hidden', label), content];
      },
    };
  }
  function previewer(parts) {
    var saved = null;
    function setAttr(node, name, value) { if (value == null) node.removeAttribute(name); else node.setAttribute(name, value); }
    return {
      apply: function (a) {
        if (!saved) {
          saved = {
            src: parts.img.getAttribute('src'),
            srcset: parts.img.getAttribute('srcset'),
            title: parts.title.textContent,
            hrefs: parts.links.map(function (l) { return l.getAttribute('href'); }),
            price: null,
          };
        }
        if (a.dataset.img) {
          parts.img.setAttribute('srcset', a.dataset.srcset || '');
          parts.img.setAttribute('src', a.dataset.img);
        }
        if (parts.second) parts.second.style.display = 'none';
        if (a.dataset.title) parts.title.textContent = a.dataset.title;
        if (parts.price && a.dataset.price) {
          var old = swapChildren(parts.price, parts.priceNodes(a));
          if (!saved.price) saved.price = old;
        }
        parts.links.forEach(function (l) { l.setAttribute('href', a.getAttribute('href')); });
      },
      restore: function () {
        if (!saved) return;
        setAttr(parts.img, 'srcset', saved.srcset);
        setAttr(parts.img, 'src', saved.src);
        if (parts.second) parts.second.style.display = '';
        parts.title.textContent = saved.title;
        if (saved.price) swapChildren(parts.price, saved.price);
        parts.links.forEach(function (l, i) { setAttr(l, 'href', saved.hrefs[i]); });
        saved = null;
      },
    };
  }

  function initCard(el) {
    if (!CFG.preview) return; // swatches stay plain links
    var remote = el.closest('remote-product-card');
    var boost = remote ? null : el.closest('.boost-sd__product-item');
    var parts = remote ? null : boost ? boostCard(boost) : themeCard(el.closest('.grid-product') || el.parentNode);
    var view = parts ? previewer(parts) : null;
    if (!remote && !view) return; // card markup not recognised: plain links
    var pointer = 'touch', selected = null;
    function mark(a) {
      if (selected) selected.classList.remove('is-selected');
      selected = a;
      if (a) a.classList.add('is-selected');
    }
    el.addEventListener('pointerdown', function (ev) { pointer = ev.pointerType || 'touch'; }, { passive: true });
    el.addEventListener('click', function (ev) {
      var a = ev.target.closest('.colorways__swatch');
      // keyboard (detail 0), mouse, the current colour and a second tap all open the product
      if (!a || ev.detail === 0 || pointer === 'mouse' || a.classList.contains('is-current') || a === selected) return;
      ev.preventDefault();
      ev.stopPropagation();
      if (remote) { // DY card: re-render the whole card as this colour (assets/dy-remote-product-card.js)
        remote.removeAttribute('variant-id');
        remote.setAttribute('handle', a.getAttribute('data-handle'));
        return;
      }
      view.apply(a);
      mark(a);
    });
    if (view && window.matchMedia && window.matchMedia('(hover: hover)').matches) {
      el.addEventListener('pointerover', function (ev) {
        var a = ev.target.closest('.colorways__swatch');
        if (!a || ev.pointerType !== 'mouse') return;
        if (a.classList.contains('is-current')) view.restore(); else view.apply(a);
      });
      el.addEventListener('pointerleave', function (ev) {
        if (ev.pointerType === 'mouse' && !selected) view.restore();
      });
    }
  }

  /* ------------------------------------------------------------------- pdp */
  function initPdp(el) {
    var sizes = {};
    try { sizes = JSON.parse((el.querySelector('script[data-colorway-sizes]') || {}).textContent || '{}'); } catch (e) { sizes = {}; }
    var nameEl = el.querySelector('[data-colorway-name]');
    var name = nameEl ? nameEl.textContent : '';
    var sizeIndex = parseInt(el.getAttribute('data-size-index'), 10);
    var section = el.closest('[data-section-type="product"]');
    var prefetched = {};
    // Point each colour at the variant with the size the shopper picked, when it is in stock.
    function carry(size) {
      el.querySelectorAll('.colorways__swatch:not(.is-current)').forEach(function (a) {
        var base = a.getAttribute('href').split('?')[0];
        var bySize = sizes[a.getAttribute('data-handle')];
        var id = size && bySize && bySize[size];
        a.setAttribute('href', id ? base + '?variant=' + id : base);
      });
    }
    if (section && sizeIndex >= 0) {
      section.addEventListener('variantChange', function (ev) { // assets/theme.js variant selection
        var v = ev.detail && ev.detail.variant;
        if (v && v.options) carry(v.options[sizeIndex]);
      });
      if (el.getAttribute('data-selected-size')) carry(el.getAttribute('data-selected-size'));
    }
    function showName(a) { if (nameEl) nameEl.textContent = a ? a.getAttribute('data-name') || name : name; }
    // Load another colour's page in the background when the shopper rests on its swatch (mouse), presses it
    // (touch) or tabs to it, so opening it is faster. The current href is used, so a picked size is included.
    function prefetch(a) {
      var href = a && !a.classList.contains('is-current') && a.getAttribute('href');
      if (!href || prefetched[href]) return;
      prefetched[href] = true;
      var n;
      if (SPECULATION) { // Chromium: kept for the next navigation even though product pages are not cacheable
        n = document.createElement('script');
        n.type = 'speculationrules';
        n.textContent = JSON.stringify({ prefetch: [{ source: 'list', urls: [href] }] });
      } else {
        n = document.createElement('link');
        n.rel = 'prefetch';
        n.href = href;
      }
      document.head.appendChild(n);
    }
    var hover = 0;
    el.addEventListener('pointerover', function (ev) {
      if (ev.pointerType !== 'mouse') return;
      var a = ev.target.closest('.colorways__swatch');
      showName(a);
      clearTimeout(hover);
      if (a) hover = setTimeout(function () { prefetch(a); }, CFG.hoverPrefetchMs); // a pause, not a pass across
    });
    el.addEventListener('pointerleave', function (ev) {
      if (ev.pointerType !== 'mouse') return;
      clearTimeout(hover);
      showName(null);
    });
    el.addEventListener('focusin', function (ev) { prefetch(ev.target.closest('.colorways__swatch')); });
    el.addEventListener('pointerdown', function (ev) {
      var a = ev.target.closest('.colorways__swatch');
      if (!a || a.classList.contains('is-current')) return;
      showName(a);
      prefetch(a);
    });
  }

  /* ------------------------------------------------------------- hydration */
  var io = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (!en.isIntersecting) return;
      io.unobserve(en.target);
      en.target.hydrate();
    });
  }, { rootMargin: CFG.rootMargin }) : null;

  function render(el, answer) {
    if (!el.isConnected) return;
    var handle = el.getAttribute('data-handle');
    if (!answer.handles || answer.handles.indexOf(handle) < 0) return el.setAttribute('data-state', 'empty');
    el.innerHTML = answer.html;
    var n = decorate(el, handle, el.getAttribute('data-url'));
    el.setAttribute('data-state', n > 1 ? 'ready' : 'empty');
    if (n > 1) initCard(el);
  }

  class ColorwaySwatches extends HTMLElement {
    connectedCallback() {
      if (this._cw) return;
      this._cw = true;
      if (this.hasAttribute('data-hydrate')) {
        // Boost re-rendered the grid (filter, sort, page) or the shopper came back: no request
        var hit = lookup(this.getAttribute('data-handle'));
        if (hit) return render(this, hit);
        this.setAttribute('data-state', 'pending');
        if (io) io.observe(this); else this.hydrate();
        return;
      }
      if (this.getAttribute('data-context') === 'pdp') initPdp(this);
      else if (this.getAttribute('data-context') === 'card') initCard(this);
    }
    disconnectedCallback() {
      if (io) io.unobserve(this);
      if (this.getAttribute('data-state') === 'pending') this._cw = false; // moved before hydrating
    }
    hydrate() {
      var el = this;
      el.setAttribute('data-state', 'loading');
      resolve(el.getAttribute('data-handle'), el.getAttribute('data-key') || '').then(
        function (answer) { render(el, answer); },
        function () { el.setAttribute('data-state', 'error'); }
      );
    }
  }
  if (!customElements.get('colorway-swatches')) customElements.define('colorway-swatches', ColorwaySwatches);

  window.Colorways = {
    resolve: resolve,
    lookup: lookup,
    stats: function () { return { active: active, maxActive: maxActive, queued: queue.length, strikes: strikes, pausedUntil: pausedUntil }; },
  };
})();
